<template>
  <div class="files-page">
    <!-- Индикатор сохраненного состояния -->
    <div v-if="hasActiveImport" class="col-12 mb-3">
      <Message severity="info" :closable="false">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-info-circle"></i>
            <span><strong>Состояние сохранено!</strong> Вы можете переключаться между страницами - прогресс импорта не потеряется.</span>
          </div>
          <Button 
            label="Сбросить" 
            icon="pi pi-trash" 
            class="p-button-sm p-button-outlined p-button-danger"
            @click="resetImport"
          />
        </div>
      </Message>
    </div>
    
    <div class="grid">
      <div class="col-12">
        <Card>
          <template #title>
            <div class="flex align-items-center">
              <i class="pi pi-upload mr-2"></i>
              Загрузка файлов
            </div>
          </template>

          <template #content>
            <div class="upload-section mb-4">
              <h2 class="text-900 mb-3">Загрузка Excel файла с данными очков</h2>
              <FileUpload
                ref="excelUpload"
                mode="basic"
                accept=".xlsx,.xls"
                :maxFileSize="1073741824"
                chooseLabel="Выбрать Excel файл"
                cancelLabel="Отмена"
                @select="onExcelSelect"
              />
              <div v-if="selectedExcelFile" class="mt-3">
                <p class="text-sm text-600">Выбран файл: {{ selectedExcelFile.name }}</p>
                <Button label="Импортировать Excel" @click="onExcelUpload" :loading="isUploadingExcel" />
              </div>
              <small class="text-500 mt-1 block">
                Максимальный размер: 1GB. Поддерживаемые форматы: .xlsx, .xls
              </small>
            </div>
          </template>
        </Card>
      </div>

      <!-- Загрузка данных линз (фиксированный формат, без маппинга) -->
      <div class="col-12">
        <Card>
          <template #title>
            <div class="flex align-items-center">
              <i class="pi pi-eye mr-2"></i>
              Загрузка данных линз
            </div>
          </template>

          <template #content>
            <div class="upload-section mb-2">
              <h2 class="text-900 mb-3">Загрузка Excel файла с данными линз</h2>
              <p class="text-sm text-600 mb-3">
                Формат фиксированный (мастер-файл линз) — маппинг колонок не требуется.
                Данные пишутся в каталог «Линзы». Совпадение по штрихкоду (EAN) обновляет строку.
              </p>
              <FileUpload
                ref="lensUpload"
                mode="basic"
                accept=".xlsx,.xls"
                :maxFileSize="1073741824"
                chooseLabel="Выбрать Excel файл линз"
                cancelLabel="Отмена"
                @select="onLensSelect"
              />
              <div v-if="selectedLensFile" class="mt-3">
                <p class="text-sm text-600">Выбран файл: {{ selectedLensFile.name }}</p>
                <Button label="Импортировать линзы" icon="pi pi-upload" @click="onLensUpload" :loading="isUploadingLens" />
              </div>
              <small class="text-500 mt-1 block">
                Максимальный размер: 1GB. Поддерживаемые форматы: .xlsx, .xls
              </small>

              <Message v-if="lensUploadResult" severity="success" :closable="true" class="mt-3">
                Импорт линз завершён: добавлено <strong>{{ lensUploadResult.inserted }}</strong>,
                обновлено <strong>{{ lensUploadResult.updated }}</strong>,
                ошибок <strong>{{ lensUploadResult.failed }}</strong>
                (всего строк: {{ lensUploadResult.total }}).
              </Message>
            </div>
          </template>
        </Card>
      </div>

            <!-- Маппинг колонок -->
      <div v-if="excelData.columns" class="col-12">
        <ColumnMapper
          :columns="excelData.columns"
          :initial-mapping="excelData.mapping"
          :initial-protected-fields="excelData.protected_fields"
          :sample-data="excelData.sample_data"
          :structure-info="excelData.structure_info"
          :extracted-images="excelData.extracted_images"
          :is-loading="isConfirming || isPreviewLoading"
          @mapping-changed="onMappingChanged"
          @apply-mappings="onApplyMappings"
          @request-preview="onRequestPreview"
        />
      </div>

      <div v-if="isPreviewLoading || previewResult" class="col-12">
        <Card>
          <template #title>
            <div class="flex align-items-center">
              <i class="pi pi-eye mr-2"></i>
              Предпросмотр нормализованных данных
            </div>
          </template>

          <template #content>
            <div v-if="isPreviewLoading" class="preview-loading">
              <ProgressSpinner strokeWidth="4" style="width: 60px; height: 60px" />
              <p class="mt-3 text-600">Формируем предпросмотр, пожалуйста подождите…</p>
            </div>

            <div v-else-if="previewResult">
              <div class="grid preview-stats">
                <div class="col-12 md:col-3">
                  <div class="stat-card">
                    <div class="stat-number text-primary">{{ previewResult.total_rows }}</div>
                    <div class="stat-label">Всего строк</div>
                  </div>
                </div>
                <div class="col-12 md:col-3">
                  <div class="stat-card">
                    <div class="stat-number text-green-600">{{ previewResult.valid_rows }}</div>
                    <div class="stat-label">Готовы к импорту</div>
                  </div>
                </div>
                <div class="col-12 md:col-3">
                  <div class="stat-card">
                    <div class="stat-number text-orange-600">{{ previewResult.invalid_rows }}</div>
                    <div class="stat-label">Пустые/некорректные строки</div>
                  </div>
                </div>
                <div class="col-12 md:col-3">
                  <div class="stat-card">
                    <div class="stat-number text-blue-600">{{ previewTableRows.length }}</div>
                    <div class="stat-label">Показано строк (макс {{ previewLimit }})</div>
                  </div>
                </div>
              </div>

              <div class="mb-4" v-if="warningSummaryEntries.length">
                <Message severity="warn" :closable="false">
                  <div class="flex align-items-start">
                    <i class="pi pi-exclamation-triangle mr-2 mt-1"></i>
                    <div>
                      <strong>Предупреждения предпросмотра</strong>
                      <ul class="p-0 m-0 preview-summary-list">
                        <li v-for="([warning, count]) in warningSummaryEntries" :key="warning">
                          {{ warning }} — <strong>{{ count }}</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Message>
              </div>

              <div class="mb-4" v-if="missingMappingsEntries.length">
                <Message severity="info" :closable="false">
                  <div class="flex align-items-start">
                    <i class="pi pi-lightbulb mr-2 mt-1"></i>
                    <div>
                      <strong>Отсутствуют канонические значения</strong>
                      <p class="text-600 mb-2">Добавьте сопоставления для следующих значений, чтобы устранить предупреждения:</p>
                      <div class="missing-mapping-grid">
                        <div v-for="([attribute, values]) in missingMappingsEntries" :key="attribute" class="missing-mapping-card">
                          <h5 class="mb-2">{{ attribute }}</h5>
                          <ul class="p-0 m-0">
                            <li v-for="value in values" :key="value">{{ value }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </Message>
              </div>

              <div class="mb-3" v-if="errorSummaryEntries.length">
                <Message severity="error" :closable="false">
                  <div class="flex align-items-start">
                    <i class="pi pi-times-circle mr-2 mt-1"></i>
                    <div>
                      <strong>Ошибки валидации</strong>
                      <ul class="p-0 m-0 preview-summary-list">
                        <li v-for="([error, count]) in errorSummaryEntries" :key="error">
                          {{ error }} — <strong>{{ count }}</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Message>
              </div>

              <div class="preview-table-wrapper" v-if="previewTableRows.length">
                <DataTable
                  :value="previewTableRows"
                  :scrollable="true"
                  scrollHeight="400px"
                  class="p-datatable-sm preview-table"
                  :rowClass="getPreviewRowClass"
                >
                  <Column field="rowNumber" header="Строка" style="width: 90px" />
                  <Column header="Готовность" style="width: 140px">
                    <template #body="slotProps">
                      <Tag
                        :value="slotProps.data.isValid ? 'Готова' : 'Нет данных'"
                        :severity="slotProps.data.isValid ? 'success' : 'danger'"
                      />
                    </template>
                  </Column>
                  <Column
                    v-for="column in previewColumns"
                    :key="column"
                    :field="column"
                    :header="getDbFieldLabel(column)"
                    style="min-width: 160px"
                  />
                  <Column header="Предупреждения" style="min-width: 220px">
                    <template #body="slotProps">
                      <div v-if="slotProps.data.warnings.length === 0" class="text-500">—</div>
                      <div v-else class="warnings-list">
                        <Tag
                          v-for="warning in slotProps.data.warnings"
                          :key="warning"
                          value=""
                          severity="warning"
                          class="mb-2 mr-2"
                        >
                          {{ warning }}
                        </Tag>
                      </div>
                    </template>
                  </Column>
                </DataTable>
                <p class="text-500 text-sm mt-2">Показаны первые {{ previewTableRows.length }} строк из {{ previewResult.total_rows }}.</p>
              </div>

              <div v-else class="text-center py-4 text-600">
                Нет данных для предпросмотра с текущим маппингом.
              </div>

              <div class="flex justify-content-end mt-4 gap-2">
                <Button label="Очистить предпросмотр" icon="pi pi-times" severity="secondary" @click="clearPreview" />
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- Модальное окно прогресса и результатов импорта -->
      <Dialog 
        v-model:visible="showImportModal" 
        modal 
        header="Импорт данных"
        :style="{ width: '52rem', maxWidth: '95vw' }"
        :breakpoints="{ '1199px': '80vw', '575px': '95vw' }"
        :closable="!importInProgress"
      >
        <div class="import-progress">
          <ul class="import-steps">
            <li 
              v-for="step in importSteps" 
              :key="step.key" 
              class="import-step"
              :class="step.status"
            >
              <div class="step-icon-wrapper">
                <i :class="['step-icon', getStepIcon(step.status)]"></i>
              </div>
              <div class="step-content">
                <div class="step-header">
                  <span class="step-title">{{ step.title }}</span>
                  <Tag 
                    v-if="step.status === 'active'" 
                    value="В работе" 
                    severity="info"
                    rounded
                  />
                  <Tag 
                    v-else-if="step.status === 'done'" 
                    value="Готово" 
                    severity="success"
                    rounded
                  />
                  <Tag 
                    v-else-if="step.status === 'error'" 
                    value="Ошибка" 
                    severity="danger"
                    rounded
                  />
                  <Tag 
                    v-else 
                    value="Ожидание" 
                    severity="secondary"
                    rounded
                  />
                </div>
                <p class="step-description">{{ step.description }}</p>
                <p v-if="step.note" class="step-note">{{ step.note }}</p>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="importError" class="import-error">
          <Message severity="error" :closable="false">
            <i class="pi pi-times-circle mr-2"></i>
            {{ importError }}
          </Message>
        </div>

        <div v-else-if="importCompleted">
          <Message severity="success" :closable="false" class="mb-4">
            <div class="import-summary-header">
              <i class="pi pi-check-circle mr-2"></i>
              {{ confirmationResults.summary }}
            </div>
            <div v-if="formattedImportDuration" class="import-duration">
              Время обработки: {{ formattedImportDuration }}
            </div>
          </Message>

          <div class="import-summary-grid mb-4">
            <div class="import-summary-card success">
              <span class="label">Новых записей</span>
              <span class="value">{{ formatInteger(confirmationResults.inserted) }}</span>
            </div>
            <div class="import-summary-card info">
              <span class="label">Обновлено</span>
              <span class="value">{{ formatInteger(confirmationResults.updated) }}</span>
            </div>
            <div class="import-summary-card warning" v-if="confirmationResults.rejected_count > 0">
              <span class="label">Отклонено</span>
              <span class="value">{{ formatInteger(confirmationResults.rejected_count) }}</span>
              <small class="muted">Без part_number — можно скачать и исправить</small>
            </div>
            <div class="import-summary-card" v-if="totalProcessed">
              <span class="label">Всего обработано</span>
              <span class="value">{{ formatInteger(totalProcessed) }}</span>
            </div>
          </div>

          <div v-if="backgroundTasks.length" class="import-background mb-4">
            <h4>Запущены фоновые задачи</h4>
            <ul>
              <li v-for="task in backgroundTasks" :key="task.key">
                <i class="pi pi-history mr-2 text-primary"></i>
                {{ task.label }} — task_id: <code>{{ task.taskId }}</code>
              </li>
            </ul>
          </div>

          <div v-if="confirmationResults.errors && confirmationResults.errors.length > 0" class="import-warnings">
            <h4 class="text-orange-600 mb-3 flex align-items-center">
              <i class="pi pi-exclamation-triangle mr-2"></i>
              Предупреждения (первые {{ Math.min(confirmationResults.errors.length, 10) }})
            </h4>
            <div class="surface-50 p-3 border-round-md border-left-3 border-orange-500 max-h-20rem overflow-y-auto">
              <ul class="list-none p-0 m-0">
                <li v-for="error in confirmationResults.errors" :key="error" class="text-orange-700 mb-1 text-sm">
                  <i class="pi pi-info-circle mr-2"></i>
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div v-else class="import-pending">
          <ProgressSpinner strokeWidth="4" style="width: 4rem; height: 4rem" />
          <p class="mt-3 mb-0">Импорт выполняется. Для больших файлов это может занять несколько минут.</p>
        </div>

        <template #footer>
          <div class="import-footer">
            <Button 
              label="Скачать ненайденные строки" 
              icon="pi pi-download" 
              class="p-button-outlined p-button-secondary"
              v-if="hasRejectedRows"
              @click="downloadRejectedRows"
            />
            <div class="footer-spacer"></div>
            <Button 
              label="Импортировать ещё файлы" 
              icon="pi pi-upload" 
              class="p-button-text"
              :disabled="importInProgress"
              @click="startNewImport"
            />
            <Button 
              label="Закрыть" 
              icon="pi pi-times" 
              :disabled="importInProgress"
              @click="closeImportModal"
            />
          </div>
        </template>
      </Dialog>
    </div>
    <!-- Toast для уведомлений -->
    <Toast />
    <ConfirmDialog />
  </div>
</template>

<script>
import { computed, ref, reactive, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import Card from 'primevue/card'
import FileUpload from 'primevue/fileupload'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import Dialog from 'primevue/dialog'
import ConfirmDialog from 'primevue/confirmdialog'
import Toast from 'primevue/toast'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import apiService from '../services/apiService'
import ColumnMapper from '../components/ColumnMapper.vue'
import { useFilesStore } from '../stores/filesStore'

// Шаг 2 (нормализация значений) убран из импорта: значения type/gender/age/shape/material
// грузятся из документа как есть. Пустой список оставлен для совместимости со старой логикой.
const STATIC_ATTRIBUTES = []

export default {
  name: 'FilesPage',
  components: {
    Card,
    FileUpload,
    DataTable,
    Column,
    Button,
    Dropdown,
    Message,
    Dialog,
    ConfirmDialog,
    Toast,
    Tag,
    ProgressSpinner,
    ColumnMapper
  },
  setup() {
    const toast = useToast()
    const confirm = useConfirm()
    const filesStore = useFilesStore()
    
    // Получаем состояние из store
    const {
      selectedExcelFile,
      isUploadingExcel,
      excelData,
      currentStep,
      isConfirming,
      isPreviewLoading,
      previewResult,
      confirmationResults,
      valueMappings,
      unresolvedValueMappings,
      staticAttributeUniqueValues,
      selectedStaticAttributes,
      importInProgress,
      importError,
      importStartedAt,
      importDurationMs,
      importSteps,
      showImportModal,
      hasActiveImport
    } = storeToRefs(filesStore)
    
    // Локальные refs (не сохраняются)
    const excelUpload = ref()
    const sampleData = ref([])
    const locale = ref('ru')
    const previewLimit = 200
    
    // Методы из store
    const { setStepStatus } = filesStore
    
    // Метод для сброса импорта
    const resetImport = () => {
      confirm.require({
        message: 'Вы уверены, что хотите сбросить текущий импорт? Все несохраненные данные будут потеряны.',
        header: 'Подтверждение',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Да, сбросить',
        rejectLabel: 'Отмена',
        accept: () => {
          filesStore.resetImportState()
          toast.add({
            severity: 'success',
            summary: 'Сброс выполнен',
            detail: 'Состояние импорта очищено',
            life: 3000
          })
        }
      })
    }
    
    const createDefaultImportSteps = filesStore.createDefaultImportSteps

    const getActiveStepKey = () => {
      const active = importSteps.value.find(step => step.status === 'active')
      if (active) {
        return active.key
      }
      const pending = importSteps.value.find(step => step.status === 'pending')
      return pending ? pending.key : null
    }

    const resetImportProgress = () => {
      filesStore.resetImportProgress()
    }

    const formatInteger = (value) => {
      return new Intl.NumberFormat('ru-RU').format(value || 0)
    }

    const getStepIcon = (status) => {
      switch (status) {
        case 'done':
          return 'pi pi-check-circle text-green-500'
        case 'active':
          return 'pi pi-spinner pi-spin text-primary'
        case 'error':
          return 'pi pi-times-circle text-red-500'
        default:
          return 'pi pi-clock text-400'
      }
    }

    const importCompleted = computed(() => !importInProgress.value && !importError.value && !!confirmationResults.value)

    const hasRejectedRows = computed(() => {
      return Boolean(
        confirmationResults.value?.rejected_count > 0 &&
        confirmationResults.value?.rejected_file_token
      )
    })

    const totalProcessed = computed(() => {
      if (!confirmationResults.value) {
        return 0
      }
      return (confirmationResults.value.inserted || 0) + (confirmationResults.value.updated || 0)
    })

    const formattedImportDuration = computed(() => {
      if (!importDurationMs.value) {
        return null
      }
      const seconds = Math.floor(importDurationMs.value / 1000)
      const minutes = Math.floor(seconds / 60)
      const remainSeconds = seconds % 60
      const milliseconds = importDurationMs.value % 1000
      if (minutes > 0) {
        return `${minutes} мин ${remainSeconds.toString().padStart(2, '0')} сек`
      }
      if (seconds > 0) {
        return `${seconds}.${Math.floor(milliseconds / 100)} сек`
      }
      return `${importDurationMs.value} мс`
    })

    const backgroundTaskLabels = {
      description_generation: 'Генерация описаний',
      image_sync: 'Синхронизация изображений',
      analytics: 'Аналитические расчёты'
    }

    const backgroundTasks = computed(() => {
      const tasks = confirmationResults.value?.background_tasks || {}
      return Object.entries(tasks).map(([key, taskId]) => ({
        key,
        taskId,
        label: backgroundTaskLabels[key] || key
      }))
    })

    const dbFields = [
      { label: 'Бренд (CP)', value: 'brand_cp' },
      { label: 'Артикул (Part Number)', value: 'part_number' },
      { label: 'Название детали', value: 'part_name' },
      { label: 'Категория', value: 'category' },
      { label: 'Тип', value: 'type' },
      { label: 'Размер', value: 'size' },
      { label: 'Цвет', value: 'color' },
      { label: 'Бренд', value: 'brand' },
      { label: 'Производитель', value: 'producer' },
      { label: 'Страна', value: 'country' },
      { label: 'Пол', value: 'gender' },
      { label: 'Переносица (Bridge)', value: 'bridge' },
      { label: 'Длина заушника (Temple)', value: 'temple_length' },
      { label: 'Высота линзы', value: 'lens_height' },
      { label: 'Возраст', value: 'age' },
      { label: 'Форма', value: 'shape' },
      { label: 'Год', value: 'year' },
      { label: 'Модель', value: 'model' },
      { label: 'Состояние', value: 'condition' },
      { label: 'Статус на сайте', value: 'site_status' },
      { label: 'Новинка', value: 'is_new' },
      { label: 'Бестселлер', value: 'is_bestseller' },
      { label: 'Rx', value: 'is_rx' },
      { label: 'Polarized', value: 'is_polarized' },
      { label: 'Photochrome', value: 'is_photochromic' },
      { label: 'Материал', value: 'material' },
      { label: 'РРЦ', value: 'rrp' },
      { label: 'Оптовая цена', value: 'wholesale_price' },
      { label: 'Валюта', value: 'currency' },
      { label: 'Название цвета', value: 'color_name' },
      { label: 'Цвет фронта', value: 'frontal_color' },
      { label: 'Цвет заушника', value: 'temple_color' },
      { label: 'Базовый цвет линзы', value: 'base_lens_color' },
      { label: 'Тип линзы', value: 'lens_type' },
      { label: 'Материал линзы', value: 'lens_material' },
      { label: 'Обработка линзы', value: 'lens_treatment' },
      { label: 'Светопропускание, %', value: 'transmission_percentage' },
      { label: 'Категория фильтра', value: 'filter_protection' },
      { label: 'Материал заушника', value: 'temple_material' },
      { label: 'Посадка', value: 'fitting' },
      { label: 'Дизайн переносицы', value: 'bridge_design' },
      { label: 'Посадка переносицы', value: 'bridge_fit' },
      { label: 'Тип ободка', value: 'rim_type' },
      { label: 'Покрытие/отделка', value: 'finish' },
      { label: 'Ширина линзы', value: 'lens_width' },
      { label: 'Диагональ', value: 'diagonal' },
      { label: 'Между шарнирами', value: 'hinge_to_hinge' },
      { label: 'Эффективный диаметр', value: 'effective_diameter' },
      { label: 'Возрастная группа', value: 'age_group' },
      { label: 'Форма лица', value: 'face_shape' },
      { label: 'Маркетинговая тема', value: 'marketing_theme' },
      { label: 'Сезон релиза', value: 'release_season' },
      { label: 'Код релиза', value: 'release_code' },
      { label: 'Дата доступности', value: 'availability_date' },
      { label: 'Дата релиза', value: 'release_date' }
    ]

    const showValueMappingPanel = computed(() => {
      return Object.keys(staticAttributeUniqueValues.value || {}).length > 0
    })

    const filteredUniqueValues = computed(() => {
      const uniqueValues = staticAttributeUniqueValues.value || {}
      if (!selectedStaticAttributes.value || selectedStaticAttributes.value.length === 0) {
        return uniqueValues
      }

      return selectedStaticAttributes.value.reduce((acc, attribute) => {
        if (uniqueValues[attribute]) {
          acc[attribute] = uniqueValues[attribute]
        }
        return acc
      }, {})
    })

    const availableStaticAttributes = computed(() => {
      return Object.keys(staticAttributeUniqueValues.value || {})
    })

    const previewColumns = computed(() => {
      return previewResult.value?.mapped_columns || []
    })

    const previewTableRows = computed(() => {
      if (!previewResult.value) {
        return []
      }

      return previewResult.value.preview_rows.map(row => ({
        rowNumber: row.row_number,
        isValid: row.is_valid,
        warnings: row.warnings || [],
        ...row.data
      }))
    })

    const warningSummaryEntries = computed(() => {
      if (!previewResult.value) return []
      return Object.entries(previewResult.value.warning_summary || {})
    })

    const errorSummaryEntries = computed(() => {
      if (!previewResult.value) return []
      return Object.entries(previewResult.value.error_summary || {})
    })

    const missingMappingsEntries = computed(() => {
      if (!previewResult.value) return []
      return Object.entries(previewResult.value.missing_mappings || {})
    })

    const buildInitialValueMappings = (uniqueValuesOverride) => {
      const uniqueValues = uniqueValuesOverride || staticAttributeUniqueValues.value || {}
      const storedRules = excelData.value.value_mapping_rules || {}
      const initial = {}

      Object.entries(uniqueValues).forEach(([attribute, values]) => {
        if (!Array.isArray(values)) {
          return
        }

        const attributeRules = {}
        const existingRules = storedRules[attribute] || {}
        values.forEach((rawValue) => {
          if (rawValue === null || rawValue === undefined) {
            return
          }
          const sourceValue = String(rawValue)
          const normalizedKey = sourceValue.trim().toUpperCase()
          if (!normalizedKey) {
            return
          }
          const matchedRule =
            existingRules[normalizedKey] || existingRules[sourceValue] || null
          if (matchedRule) {
            attributeRules[sourceValue] = matchedRule
          }
        })

        initial[attribute] = attributeRules
      })

      valueMappings.value = initial
      unresolvedValueMappings.value = []
      previewResult.value = null
    }

    const onExcelSelect = (event) => {
      selectedExcelFile.value = event.files[0]
      previewResult.value = null
    }

    const onExcelUpload = async () => {
      if (!selectedExcelFile.value) return

      isUploadingExcel.value = true
      try {
        const response = await apiService.uploadExcel(selectedExcelFile.value)
        excelData.value.columns = response.data.columns
        excelData.value.mapping = response.data.mapping
        excelData.value.sample_data = response.data.sample_data
        excelData.value.structure_info = response.data.structure_info
        excelData.value.extracted_images = response.data.extracted_images
        excelData.value.column_unique_values = response.data.column_unique_values || {}
        excelData.value.value_mapping_rules = response.data.value_mapping_rules || {}
        excelData.value.file_token = response.data.file_token  // Сохраняем токен для preview/confirm
        
        // Показываем информацию об AI маппинге, если есть
        if (response.data.ai_mapping_status === 'failed' && response.data.ai_mapping_message) {
          toast.add({
            severity: 'warn',
            summary: 'ИИ-маппинг недоступен',
            detail: response.data.ai_mapping_message,
            life: 8000
          })
        }
        
  currentStep.value = 1
  staticAttributeUniqueValues.value = {}
  selectedStaticAttributes.value = []
  buildInitialValueMappings({})
        previewResult.value = null
      } catch (error) {
        console.error('Ошибка загрузки Excel:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка загрузки',
          detail: 'Не удалось загрузить Excel файл: ' + (error.response?.data?.detail || error.message),
          life: 5000
        })
      } finally {
        isUploadingExcel.value = false
      }
    }

    // ===== Импорт линз (фиксированный формат, без маппинга) =====
    const lensUpload = ref(null)
    const selectedLensFile = ref(null)
    const isUploadingLens = ref(false)
    const lensUploadResult = ref(null)

    const onLensSelect = (event) => {
      selectedLensFile.value = event.files[0]
      lensUploadResult.value = null
    }

    const onLensUpload = async () => {
      if (!selectedLensFile.value) return
      isUploadingLens.value = true
      try {
        const response = await apiService.uploadLenses(selectedLensFile.value)
        lensUploadResult.value = response.data
        toast.add({
          severity: 'success',
          summary: 'Линзы загружены',
          detail: response.data.message,
          life: 6000
        })
      } catch (error) {
        console.error('Ошибка загрузки линз:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка загрузки линз',
          detail: 'Не удалось загрузить файл линз: ' + (error.response?.data?.detail || error.message),
          life: 6000
        })
      } finally {
        isUploadingLens.value = false
      }
    }

    const onMappingChanged = (change) => {
      if (change.clearAll) {
        Object.keys(excelData.value.mapping).forEach(key => {
          delete excelData.value.mapping[key]
        })
        excelData.value.protected_fields = []
      } else if (change.mapping && change.protectedFields !== undefined) {
        // Новый формат: { mapping: {...}, protectedFields: [...] }
        excelData.value.mapping = { ...change.mapping }
        excelData.value.protected_fields = [...change.protectedFields]
      } else {
        // Старый формат для совместимости
        if (!change || !change.dbField) {
          return
        }
        if (change.excelColumn) {
          excelData.value.mapping[change.dbField] = change.excelColumn
        } else {
          delete excelData.value.mapping[change.dbField]
        }
      }
      previewResult.value = null
    }

    const onContinueStep = (finalMapping) => {
      if (!finalMapping || Object.keys(finalMapping).length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Не задан маппинг',
          detail: 'Добавьте соответствия колонок перед переходом к следующему шагу.',
          life: 4000
        })
        return
      }

      excelData.value.mapping = { ...finalMapping }

      const uniqueValuesByColumn = excelData.value.column_unique_values || {}
      const attributeValues = {}
      const autoSelected = []
      
      // Фильтруем статичные атрибуты, исключая защищённые поля
      const protectedFieldsSet = new Set(excelData.value.protected_fields || [])
      const activeStaticAttributes = STATIC_ATTRIBUTES.filter(attr => !protectedFieldsSet.has(attr))

      activeStaticAttributes.forEach((attribute) => {
        const excelColumn = finalMapping[attribute]
        if (!excelColumn) {
          return
        }
        const values = uniqueValuesByColumn[excelColumn]
        if (values && values.length) {
          attributeValues[attribute] = values
          autoSelected.push(attribute)
        }
      })

      staticAttributeUniqueValues.value = attributeValues

      const previousSelected = new Set(selectedStaticAttributes.value)
      let nextSelected = [...autoSelected]
      if (previousSelected.size > 0) {
        const preserved = autoSelected.filter((attribute) => previousSelected.has(attribute))
        if (preserved.length > 0) {
          nextSelected = preserved
        }
      }

      selectedStaticAttributes.value = nextSelected
      buildInitialValueMappings(attributeValues)
      previewResult.value = null

      // Информация о защищённых статичных атрибутах
      const protectedStaticAttrs = STATIC_ATTRIBUTES.filter(attr => protectedFieldsSet.has(attr) && finalMapping[attr])
      if (protectedStaticAttrs.length > 0) {
        toast.add({
          severity: 'info',
          summary: 'Защищённые атрибуты пропущены',
          detail: `${protectedStaticAttrs.join(', ')} - защищены и не требуют нормализации значений.`,
          life: 5000
        })
      }

      if (autoSelected.length === 0) {
        const message = protectedStaticAttrs.length > 0
          ? 'Все статичные атрибуты защищены. Можно сразу перейти к импорту.'
          : 'Статичные атрибуты не найдены. Можно сразу перейти к предпросмотру или импорту данных.'
        
        toast.add({
          severity: 'info',
          summary: 'Статичные атрибуты не найдены',
          detail: message,
          life: 4000
        })
      } else {
        toast.add({
          severity: 'success',
          summary: 'Шаг 1 завершен',
          detail: 'Проверьте и нормализуйте статичные атрибуты перед импортом.',
          life: 4000
        })
      }

      currentStep.value = 2
    }

    const onToggleStaticAttribute = (attribute) => {
      if (!staticAttributeUniqueValues.value[attribute]) {
        return
      }

      const nextSelected = new Set(selectedStaticAttributes.value)
      if (nextSelected.has(attribute)) {
        nextSelected.delete(attribute)
      } else {
        nextSelected.add(attribute)
      }
      selectedStaticAttributes.value = Array.from(nextSelected)
      previewResult.value = null
    }

    const backToMappingStep = () => {
      currentStep.value = 1
      previewResult.value = null
    }

    const buildValueMappingsPayload = () => {
      const preparedValueMappings = {}
      const activeAttributes = selectedStaticAttributes.value.length
        ? selectedStaticAttributes.value
        : []

      activeAttributes.forEach((attribute) => {
        const rules = valueMappings.value?.[attribute]
        if (!rules) {
          return
        }
        const normalizedRules = {}
        Object.entries(rules).forEach(([source, canonical]) => {
          const sourceValue = String(source || '').trim()
          const canonicalValue = String(canonical || '').trim().toUpperCase()
          if (sourceValue && canonicalValue) {
            normalizedRules[sourceValue] = canonicalValue
          }
        })
        if (Object.keys(normalizedRules).length > 0) {
          preparedValueMappings[attribute] = normalizedRules
        }
      })

      return preparedValueMappings
    }

    const onApplyMappings = async (finalMappingOverride) => {
      const finalMapping =
        finalMappingOverride && Object.keys(finalMappingOverride).length > 0
          ? finalMappingOverride
          : { ...excelData.value.mapping }

      // Проверяем наличие токена или файла
      if (!excelData.value.file_token && !selectedExcelFile.value) {
        console.error('Файл не выбран и токен отсутствует')
        toast.add({
          severity: 'error',
          summary: 'Файл не найден',
          detail: 'Пожалуйста, загрузите файл заново.',
          life: 5000
        })
        return
      }

      if (!finalMapping || Object.keys(finalMapping).length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Не задан маппинг',
          detail: 'Сначала настройте соответствия колонок перед импортом.',
          life: 4000
        })
        return
      }

      isConfirming.value = true

      confirmationResults.value = null
      importError.value = null
      importSteps.value = createDefaultImportSteps()
      setStepStatus('prepare', 'active')
      showImportModal.value = true
      importInProgress.value = true
      importStartedAt.value = Date.now()
      importDurationMs.value = null

      try {
        const preparedValueMappings = buildValueMappingsPayload()

        setStepStatus('prepare', 'done', { note: `Столбцов в маппинге: ${Object.keys(finalMapping).length}` })
        setStepStatus('normalize', 'active')

        if (unresolvedValueMappings.value.length > 0) {
          toast.add({
            severity: 'warn',
            summary: 'Незаполненные значения',
            detail: 'Некоторые значения статичных атрибутов не сопоставлены. Они будут загружены как есть.',
            life: 5000
          })
        }

        // Используем токен если есть, иначе файл (обратная совместимость)
        const fileOrToken = excelData.value.file_token || selectedExcelFile.value

        const response = await apiService.confirmMapping(
          fileOrToken,
          finalMapping,
          preparedValueMappings,
          locale.value,
          true,  // auto_generate_descriptions
          excelData.value.protected_fields || []  // Передаём защищённые поля
        )
        
        // Проверяем что ответ корректный
        if (response && response.data) {
          setStepStatus('normalize', 'done')
          setStepStatus('import', 'active')

          confirmationResults.value = response.data
          setStepStatus('import', 'done', {
            note: `Добавлено: ${formatInteger(response.data.inserted)}, обновлено: ${formatInteger(response.data.updated)}`
          })

          const tasksCount = Object.keys(response.data.background_tasks || {}).length
          setStepStatus('post', 'done', {
            note: tasksCount > 0
              ? `Запущено фоновых задач: ${tasksCount}`
              : 'Фоновые задачи не требуются'
          })

          importDurationMs.value = Date.now() - (importStartedAt.value || Date.now())
          importInProgress.value = false

          // Отображаем результаты валидации value mapping (если есть)
          if (response.data.value_mapping_validation) {
            const validation = response.data.value_mapping_validation
            
            if (validation.rejected_count > 0) {
              toast.add({
                severity: 'warn',
                summary: 'Валидация правил маппинга',
                detail: `Отклонено ${validation.rejected_count} правил из ${validation.stats.total}. Проверьте детали в результатах.`,
                life: 8000
              })
            }
            
            if (validation.errors && validation.errors.length > 0) {
              validation.errors.forEach(error => {
                toast.add({
                  severity: 'error',
                  summary: 'Ошибка валидации',
                  detail: error,
                  life: 10000
                })
              })
            }
          }

          // Показываем toast с успехом
          toast.add({
            severity: 'success',
            summary: 'Импорт завершен',
            detail: response.data.summary || 'Данные успешно импортированы',
            life: 5000
          })

          // Принудительное обновление Vue
          await nextTick()
          
          // Очищаем форму маппинга только после показа результатов
          excelData.value.columns = null
          excelData.value.mapping = {}
          excelData.value.file_token = null  // Очищаем токен
          sampleData.value = []
          excelData.value.column_unique_values = {}
          excelData.value.value_mapping_rules = {}
          valueMappings.value = {}
          unresolvedValueMappings.value = []
          selectedExcelFile.value = null
          previewResult.value = null
          staticAttributeUniqueValues.value = {}
          selectedStaticAttributes.value = []
          currentStep.value = 1
        } else {
          console.error('Некорректный ответ сервера:', response)
          throw new Error('Сервер вернул некорректный ответ')
        }
      } catch (error) {
        console.error('Ошибка подтверждения маппинга:', error)

        importDurationMs.value = Date.now() - (importStartedAt.value || Date.now())
        importInProgress.value = false

        // Показываем toast с ошибкой
        let errorMessage = 'Неизвестная ошибка'
        if (error.code === 'ECONNABORTED') {
          errorMessage = 'Запрос занимает слишком много времени. Проверьте логи сервера - данные могут быть обработаны.'
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        } else if (error.message) {
          errorMessage = error.message
        }

        importError.value = errorMessage
        const failedStepKey = getActiveStepKey()
        if (failedStepKey) {
          setStepStatus(failedStepKey, 'error', { note: errorMessage })
        }

        toast.add({
          severity: 'error',
          summary: 'Ошибка импорта',
          detail: errorMessage,
          life: 10000
        })
      } finally {
        isConfirming.value = false
      }
    }

    const startNewImport = () => {
      if (importInProgress.value) {
        return
      }
      showImportModal.value = false
      resetImportProgress()
      // Фокус на выбор файла
      if (excelUpload.value) {
        excelUpload.value.$el.querySelector('input[type="file"]')?.click()
      }
      excelData.column_unique_values = {}
      excelData.value_mapping_rules = {}
      excelData.mapping = {}
      staticAttributeUniqueValues.value = {}
      selectedStaticAttributes.value = []
      currentStep.value = 1
      valueMappings.value = {}
      unresolvedValueMappings.value = []
      selectedExcelFile.value = null
      previewResult.value = null
    }

    const downloadRejectedRows = async () => {
      if (!confirmationResults.value?.rejected_file_token) {
        return
      }
      try {
        const response = await apiService.downloadRejectedRows(
          confirmationResults.value.rejected_file_token
        )
        const blob = new Blob(
          [response.data],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        )
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `rejected_rows_${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        toast.add({
          severity: 'success',
          summary: 'Файл скачан',
          detail: 'Ненайденные строки сохранены в Excel.',
          life: 4000
        })
      } catch (error) {
        const message = error.response?.data?.detail || 'Не удалось скачать файл с отклонёнными строками'
        toast.add({
          severity: 'error',
          summary: 'Ошибка загрузки',
          detail: message,
          life: 6000
        })
      }
    }

    const closeImportModal = () => {
      if (importInProgress.value) {
        return
      }
      showImportModal.value = false
      resetImportProgress()
    }

    const onValueMappingsChange = (payload) => {
      valueMappings.value = payload.mappings || {}
      unresolvedValueMappings.value = payload.unresolved || []
      previewResult.value = null
    }

    const onRequestPreview = async (finalMappingOverride) => {
      // Проверяем наличие токена или файла
      if (!excelData.file_token && !selectedExcelFile.value) {
        toast.add({
          severity: 'warn',
          summary: 'Файл не выбран',
          detail: 'Загрузите Excel файл перед предпросмотром.',
          life: 4000
        })
        return
      }

      const finalMapping =
        finalMappingOverride && Object.keys(finalMappingOverride).length > 0
          ? finalMappingOverride
          : { ...excelData.mapping }

      if (!finalMapping || Object.keys(finalMapping).length === 0) {
        toast.add({
          severity: 'warn',
          summary: 'Не задан маппинг',
          detail: 'Сначала настройте соответствия колонок перед предпросмотром.',
          life: 4000
        })
        return
      }

      const preparedValueMappings = buildValueMappingsPayload()
      isPreviewLoading.value = true
      try {
        // Используем токен если есть, иначе файл (обратная совместимость)
        const fileOrToken = excelData.file_token || selectedExcelFile.value
        
        const response = await apiService.previewMapping(
          fileOrToken,
          finalMapping,
          preparedValueMappings,
          locale.value
        )
        previewResult.value = response.data
        if ((response.data?.warning_summary && Object.keys(response.data.warning_summary).length) || (response.data?.error_summary && Object.keys(response.data.error_summary).length)) {
          toast.add({
            severity: 'warn',
            summary: 'Предпросмотр готов',
            detail: 'Некоторые строки содержат предупреждения или ошибки, проверьте ниже.',
            life: 5000
          })
        } else {
          toast.add({
            severity: 'success',
            summary: 'Предпросмотр готов',
            detail: 'Данные успешно нормализованы, можно продолжать импорт.',
            life: 4000
          })
        }
      } catch (error) {
        console.error('Ошибка предпросмотра:', error)
        const errorMessage = error.response?.data?.detail || error.message || 'Не удалось сформировать предпросмотр'
        toast.add({
          severity: 'error',
          summary: 'Ошибка предпросмотра',
          detail: errorMessage,
          life: 6000
        })
      } finally {
        isPreviewLoading.value = false
      }
    }

    const clearPreview = () => {
      previewResult.value = null
    }

    const getPreviewRowClass = (rowData) => {
      return rowData.isValid ? '' : 'row-invalid'
    }

    const getDbFieldLabel = (fieldValue) => {
      const field = dbFields.find(f => f.value === fieldValue)
      return field ? field.label : fieldValue
    }

    return {
      excelUpload,
      excelData,
      sampleData,
      confirmationResults,
      isConfirming,
      dbFields,
      selectedExcelFile,
      isUploadingExcel,
      showImportModal,
      importSteps,
      importInProgress,
      importError,
      importCompleted,
      formattedImportDuration,
      backgroundTasks,
      totalProcessed,
      hasRejectedRows,
      previewResult,
      isPreviewLoading,
      previewColumns,
      previewTableRows,
      warningSummaryEntries,
      errorSummaryEntries,
      missingMappingsEntries,
      previewLimit,
      currentStep,
      onExcelSelect,
      onExcelUpload,
      // Импорт линз
      lensUpload,
      selectedLensFile,
      isUploadingLens,
      lensUploadResult,
      onLensSelect,
      onLensUpload,
      onMappingChanged,
      onContinueStep,
      onApplyMappings,
      startNewImport,
      downloadRejectedRows,
      closeImportModal,
      showValueMappingPanel,
      filteredUniqueValues,
      availableStaticAttributes,
      selectedStaticAttributes,
      valueMappings,
      onValueMappingsChange,
      onToggleStaticAttribute,
      backToMappingStep,
      getStepIcon,
      formatInteger,
      onRequestPreview,
      clearPreview,
      getPreviewRowClass,
      getDbFieldLabel,
      // Новые методы из store
      resetImport,
      hasActiveImport
    }
  }
}
</script>

<style scoped>
.files-page {
  max-width: 1200px;
  margin: 0 auto;
}

.upload-section {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}

.mapping-table {
  margin-top: 1rem;
}

.mapping-actions {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

.import-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.stepper-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.stepper-item.active {
  color: var(--primary-color);
}

.stepper-item.completed {
  color: var(--text-color);
}

.stepper-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 2px solid var(--surface-300);
  font-weight: 600;
  background: var(--surface-card);
  color: inherit;
}

.stepper-item.active .stepper-number {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: #fff;
}

.stepper-item.completed .stepper-number {
  border-color: var(--primary-color);
  background: rgba(63, 81, 181, 0.12);
  color: var(--primary-color);
}

.stepper-connector {
  flex: 1;
  height: 2px;
  background: var(--surface-300);
  border-radius: 999px;
}

.stepper-connector.completed {
  background: var(--primary-color);
}

.stepper-label {
  white-space: nowrap;
}

.step-two-toolbar {
  box-shadow: none;
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
}

.preview-summary-list {
  list-style: none;
  padding-left: 0;
  margin-top: 0.5rem;
}

.preview-summary-list li {
  margin-bottom: 0.25rem;
}

.missing-mapping-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
}

.missing-mapping-card {
  background: var(--surface-100);
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid var(--surface-200);
}

.missing-mapping-card h5 {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--surface-700);
}

.missing-mapping-card ul {
  list-style: none;
  padding-left: 0;
}

.missing-mapping-card li {
  font-size: 0.85rem;
  color: var(--surface-600);
  margin-bottom: 0.25rem;
}

.warnings-list {
  display: flex;
  flex-wrap: wrap;
}

.preview-table .row-invalid {
  background-color: #fff4e5;
}

.preview-table .p-tag {
  font-size: 0.75rem;
}

.import-progress {
  margin-bottom: 1.5rem;
}

.import-steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.import-step {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.import-step.pending {
  opacity: 0.8;
}

.import-step.active {
  border-color: #0b74de;
  background: #f0f7ff;
}

.import-step.done {
  border-color: #28a745;
  background: #f6fffc;
}

.import-step.error {
  border-color: #dc3545;
  background: #fff5f5;
}

.step-icon-wrapper {
  flex-shrink: 0;
}

.step-icon {
  font-size: 1.75rem;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.step-title {
  font-weight: 600;
  font-size: 1rem;
}

.step-description {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.step-note {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #495057;
}

.import-error {
  margin-bottom: 1.5rem;
}

.import-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.import-summary-card {
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.import-summary-card.success {
  border-color: #28a745;
  background: #f6fffc;
}

.import-summary-card.info {
  border-color: #0b74de;
  background: #f0f7ff;
}

.import-summary-card.warning {
  border-color: #ffc107;
  background: #fff9e6;
}

.import-summary-card .label {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

.import-summary-card .value {
  font-size: 1.6rem;
  font-weight: 600;
  color: #212529;
}

.import-summary-card .muted {
  font-size: 0.75rem;
  color: #6c757d;
}

.import-background ul {
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0;
}

.import-background li {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.35rem;
}

.import-warnings {
  margin-top: 1.5rem;
}

.import-pending {
  text-align: center;
  padding: 2rem 1rem;
  color: #6c757d;
}

.import-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.import-footer .footer-spacer {
  flex: 1;
}

.import-summary-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
}

.import-duration {
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #495057;
}
</style>
